// @flow

import * as React from "react";
import cx from "classnames";
import Transition from "react-transition-group/Transition";
import styles from "./Popover.scss";

type PopoverRenderProps = {
  closePopover: () => void,
  openPopover: () => void,
  triggerRef: {
    current: null | HTMLDivElement
  }
};

type PopoverProps = {
  input: (renderProps: PopoverRenderProps) => React.Node,
  content: (contentArgs: PopoverRenderProps) => React.Node,
  onOpen: () => void,
  onClose: () => void,
  popperWidth: number,
  onLeft: boolean,
  additionalStyles: {
    componentWrapper: string
  },
  isDisabled: boolean
};

type PopoverState = {
  isOpen: boolean
};

export const duration = 150;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0
};

const transitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: {}
};

class Popover extends React.Component<PopoverProps, PopoverState> {
  static defaultProps = {
    input: () => null,
    content: () => null,
    onOpen: () => null,
    onClose: () => null,
    popperWidth: null,
    onLeft: false,
    additionalStyles: {
      componentWrapper: ""
    },
    isDisabled: false
  };

  state = {
    isOpen: false
  };

  triggerRef = React.createRef();

  componentWrapperRef = React.createRef();

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
    document.addEventListener("focus", this.handleFocus);
    document.addEventListener("keydown", this.handleKeyPress);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    document.removeEventListener("focus", this.handleFocus);
    document.removeEventListener("keydown", this.handleKeyPress);
  }

  handleClickOutside = (e: MouseEvent) =>
    this.handleVisibilityDependingIfElementIsInWrapper(e.target);

  handleFocus = (e: FocusEvent) => this.handleVisibilityDependingIfElementIsInWrapper(e.target);

  handleKeyPress = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLElement) {
      const { closeonshifttab, closeontab } = e.target.dataset;

      const shouldCloseOnShiftTab = closeonshifttab === "true" && e.shiftKey && e.keyCode === 9;
      const shouldCloseOnTab = closeontab === "true" && !e.shiftKey && e.keyCode === 9;

      if (shouldCloseOnShiftTab || shouldCloseOnTab) {
        this.closePopover();
      }
    }
  };

  handleVisibilityDependingIfElementIsInWrapper = (element: EventTarget) =>
    this.setState(state => {
      const currentIsOpen = state.isOpen;
      const newIsOpen = this.isElementInsideWrapper(element);

      this.callOnOpenAndClose(currentIsOpen, newIsOpen);

      return {
        ...(currentIsOpen !== newIsOpen ? { isOpen: newIsOpen } : null)
      };
    });

  callOnOpenAndClose = (currentIsOpen: boolean, newIsOpen: boolean) => {
    const { onOpen, onClose } = this.props;

    if (currentIsOpen !== newIsOpen) {
      if (newIsOpen) {
        onOpen();
      } else {
        onClose();
      }
    }
  };

  setIsOpen = (isOpen: boolean) => this.setState({ isOpen });

  closePopover = () => this.setIsOpen(false);

  openPopover = () => this.setIsOpen(true);

  isElementInsideWrapper = (target: EventTarget) =>
    !!(this.componentWrapperRef.current && this.componentWrapperRef.current.contains(target));

  getPopoverRenderProps = () => ({
    closePopover: this.closePopover,
    openPopover: this.openPopover,
    triggerRef: this.triggerRef
  });

  renderInput = () => {
    const { input } = this.props;

    return input({
      onFocus: e => {
        this.handleFocus(e);
      },
      ...this.getPopoverRenderProps()
    });
  };

  renderContent = () => {
    const { content } = this.props;

    return content(this.getPopoverRenderProps());
  };

  getOnLeftStyles = () => {
    const { popperWidth } = this.props;
    const triggerWidth =
      (this.triggerRef && this.triggerRef.current && this.triggerRef.current.offsetWidth) || 0;

    return {
      marginLeft: `-${(popperWidth - triggerWidth) / 10}rem`
    };
  };

  render() {
    const { popperWidth, onLeft, additionalStyles, isDisabled } = this.props;
    const { isOpen } = this.state;

    return (
      <div
        className={cx(...[styles, additionalStyles].map(c => c.componentWrapper), {
          [styles.fixedComponentWrapper]: !!popperWidth
        })}
        ref={this.componentWrapperRef}
      >
        {this.renderInput()}
        {!isDisabled && (
          <Transition in={isOpen} timeout={duration} unmountOnExit>
            {state => {
              const stylesForTranstition =
                state in transitionStyles && typeof transitionStyles[state] === "object"
                  ? transitionStyles[state]
                  : {};

              return (
                <div
                  style={{
                    ...defaultStyle,
                    ...stylesForTranstition,
                    ...(popperWidth
                      ? {
                          maxWidth: `${popperWidth / 10}rem`
                        }
                      : {}),
                    ...(onLeft ? this.getOnLeftStyles() : {}),
                    ...(state === "exiting" ? { pointerEvents: "none" } : {})
                  }}
                  className={cx(styles.popper)}
                  data-testid="popover"
                >
                  {this.renderContent()}
                </div>
              );
            }}
          </Transition>
        )}
      </div>
    );
  }
}

export default Popover;
